import { TOTAL_COLOURWAYS, SAULEDA_COLOURS } from './sauledaColours';
import { AWNING_WARRANTY_YEARS, WARRANTY_COVER, WARRANTY_STARTS_FROM, EXPECTED_LIFE_YEARS } from '../config/warranty';
import { LEAD_STOCK, LEAD_STANDARD, LEAD_LARGE, STANDARD_SIZE_MAX_M } from '../config/leadTimes';
import { THIRD_BRACKET_FROM_M, THREE_PEOPLE_FROM_M, ANCHORS_PER_BRACKET, RECOMMENDED_MOUNTING_HEIGHT_M, BRACKET_COUNT } from '../config/installation';

// Rich product-page content, keyed by slug. A product with an entry here gets
// the editorial page: a buy box with key facts, benefit cards, a comparison,
// the videos, a spec table, reviews and an FAQ. A product without one renders
// the standard page, so this can be rolled out one model at a time.
//
// Every figure below is sourced from the installation manual, the technical
// datasheet, the Intertek wind report or the site's config modules. Nothing
// here is a marketing claim without a document behind it. When a source
// changes, change it here too.
//
// Images live under /images/products/<slug>/content/ and are optional: a
// missing file hides the image and the tile falls back to its shade, so the
// copy is never blocked on photography.

const SOLIDS = SAULEDA_COLOURS.filter((c) => c.category === 'Solid').length;
const STRIPES = SAULEDA_COLOURS.filter((c) => c.category === 'Stripe').length;

// Expected service life under normal use. Deliberately separate from the
// warranty in config/warranty.js: the warranty is what we undertake to cover,
// the service life is what the hardware is built to last. Never let copy blur
// the two.
export const AWNING_SERVICE_LIFE_YEARS = EXPECTED_LIFE_YEARS;

// What Sauleda publishes about its own fabric, narrowed to the colourways ASG
// actually stocks. Every fabric claim on an awning page reads from here, so a
// claim can always be traced rather than invented by a copywriter.
//
// Sources, both read 2026-09:
//   sauleda.com/en/who-we-are/certifications/
//   sauleda.com/en/solar-spectrum/  ("Sauleda Masacril tarpaulins block 99% of
//   UVA rays, UVB rays have a UV40-UV60-UV80 protection factor depending on
//   the color.")
//
// Sauleda rates its acrylic UV40, UV60 or UV80 against UVB under UV STANDARD
// 801, by colourway. ASG's range was selected from the UV60 and UV80
// colourways only, which is why the site states UPF 60+ (confirmed by the
// owner, 2026-09). If a UV40 colourway is ever added to the range, UPF_MIN
// has to come down with it - that is the one number to check before
// extending the colour chart.
export const UPF_MIN = 60;
export const UPF_MAX = 80;

export const SAULEDA_FACTS = {
  certifications: ['STANDARD 100 by OEKO-TEX', 'UV STANDARD 801', 'ISO 9001 since 1999', 'Made in Europe'],
  uva: 'Blocks 99% of UVA rays',
  upfShort: `UPF ${UPF_MIN}+`,
  uvb: `UPF ${UPF_MIN} to UPF ${UPF_MAX} against UVB, certified to UV STANDARD 801 - deeper colours rate highest`,
  dye: '100% acrylic, dyed through the fibre rather than printed on the surface',
  finish: 'Sauleda Fabric Protector - water and dirt repellent',
  care: 'Hose down; neutral soap at up to 30 C if needed. Dry fully before retracting',
};

// The independent marks behind the AW01, and what each one actually means.
// Deliberately specific: a certification name on its own is decoration, the
// line under it is the part a buyer can use.
export const AW01_CERTIFICATIONS = [
  {
    mark: 'EN 13561 Class 3',
    by: 'Tested by Intertek',
    what: 'Wind resistance',
    detail: 'Retractable awnings are rated 0 to 3 for wind. Class 3 is the top of that scale, and most awnings on the market carry Class 1 or Class 2. Tested at 3000 x 1530 mm.',
  },
  {
    mark: 'UV STANDARD 801',
    by: 'Sauleda fabric',
    what: 'Sun protection, measured in use',
    detail: `The strictest UV test for shade textiles. Unlike other UV standards it measures the fabric wet, stretched, abraded and aged, not only new and dry. Our colourways rate UPF ${UPF_MIN} to ${UPF_MAX}.`,
  },
  {
    mark: 'STANDARD 100 by OEKO-TEX',
    by: 'Sauleda fabric',
    what: 'No harmful substances',
    detail: 'Every component of the fabric is tested for substances harmful to health or skin, down to the thread and the coating, at every stage of manufacture.',
  },
  {
    mark: 'ISO 9001',
    by: 'Sauleda, since 1999',
    what: 'Quality management, audited',
    detail: 'The mill\'s process control is externally audited against the global quality-management standard rather than self-declared. Sauleda has held it for more than 25 years.',
  },
  {
    mark: 'Made in Europe',
    by: 'Barcelona, Spain',
    what: 'Where the fabric is made',
    detail: 'The acrylic is woven, dyed and finished in Sauleda\'s own plant in Barcelona under EU safety, health and environmental rules - not sourced on price.',
  },
];

const img = (slug, name) => `/images/products/${slug}/content/${name}.webp`;

const AW01 = 'aw01-full-box-awning';

// The AW01 installation guide video. Defined once here and used by the AW01
// page's installation section and by /for-installers, so a re-encode is a
// one-line change. `duration` is a label for the play button, not a timing.
export const AW01_INSTALL_VIDEO = {
  mp4: '/uploads/content/aw01-installation-guide.mp4?v=1',
  poster: '/uploads/content/aw01-installation-guide-poster.webp?v=1',
  title: 'AW01 installation guide',
  alt: 'AW01 installation guide - a three-minute walk-through from unpacking to setting the pitch',
  duration: '3 min',
};

// The shape a product page is built from, block by block, top to bottom:
//
//   hero          subhead, the "included" line, six key facts, trust row
//   why           six benefit cards, then the certification line
//   comparison    "what to check on any awning quote"
//   video         the demonstration clip and one caption
//   installation  four lines, the guide links and the install video
//   specs         one grouped table of hard numbers (downloads sit under it)
//   reviews       real reviews; until there are some, real install photos
//   faq           grouped questions (the quick-question form sits under it)
//
// The AW01 page was cut from fourteen blocks to these nine in 2026-09 so
// every claim is made once. Before adding a section, check the claim is not
// already made in one of these.

export const PRODUCT_CONTENT = {
  [AW01]: {
    hero: {
      subhead: 'A motorised cassette awning with built-in LED lighting, made to your size and dispatched from Melbourne.',
      included: 'Wind sensor, remote, LED lighting and wall fixings included.',
      // Six, in two columns. Keep each to one short line.
      keyFacts: [
        { icon: 'wind', text: 'Class 3 wind rating, the highest' },
        { icon: 'uv', text: `${SAULEDA_FACTS.upfShort} Sauleda fabric` },
        { icon: 'fabric', text: `${TOTAL_COLOURWAYS} colours, one price` },
        { icon: 'size', text: 'Up to 7 × 3.5 m' },
        { icon: 'plug', text: 'DIY install, plugs into a power point' },
        { icon: 'cert', text: `${AWNING_WARRANTY_YEARS}-year warranty, built to last ${AWNING_SERVICE_LIFE_YEARS}+ years` },
      ],
      // Facts about the business, shown under the buttons. A star rating
      // joins the front of this row automatically once `reviews.items` has
      // real reviews in it.
      trust: ['Assembled in Hoppers Crossing', 'Reply within 1 business day'],
    },

    why: {
      title: 'Why the AW01',
      cards: [
        {
          heading: 'Rated for real wind',
          text: 'Tested to Class 3, the top of the European wind standard. The wind sensor is included and pulls it in when you\'re not home.',
          image: img(AW01, 'h-01-wind'),
        },
        {
          heading: 'Shade that stays shade',
          text: `Spanish Sauleda acrylic, ${SAULEDA_FACTS.upfShort}, blocks 99% of UVA. The colour is dyed through the fibre, so it doesn't wash out.`,
          image: img(AW01, 'h-04-sauleda'),
        },
        {
          heading: `${TOTAL_COLOURWAYS} colours, one price`,
          text: `${SOLIDS} solids and ${STRIPES} stripes, all the same price.`,
          image: img(AW01, 'h-06-colours'),
          link: { to: '/colours', label: 'See every colour' },
        },
        {
          heading: 'Sealed when it\'s closed',
          text: 'Fabric, arms and roller fold into the cassette under a built-in rain hood. Nothing left in the weather, nothing to rattle.',
          image: img(AW01, 'h-03-cassette'),
        },
        {
          // Warm white, 3000K, confirmed by the owner 2026-09-27. The
          // technical datasheet said 5000K and was corrected to match.
          heading: 'Lit after sunset',
          text: 'Warm-white LED strips along the cassette and each arm, switched separately from the awning. Powered from the unit, no extra wiring.',
          image: img(AW01, 'h-02-led-v2'),
        },
        {
          heading: 'Button, remote or crank',
          text: 'Quiet motor with remote supplied. Adjustable pitch. Hand crank in the box for power cuts.',
          image: img(AW01, 'h-05-motor'),
        },
      ],
      // One small line of proof under the cards; the detail of each mark opens
      // underneath it. `download` names the document to link, matched against
      // the product's documents by label.
      certifications: {
        lead: 'Independently tested',
        marks: ['EN 13561 Class 3 by Intertek', 'UV STANDARD 801', 'STANDARD 100 by OEKO-TEX'],
        items: AW01_CERTIFICATIONS,
        download: { match: /wind/i, label: 'Download the wind report' },
      },
    },

    // Each row is a question a buyer can put to any supplier. The right-hand
    // column says "often", not "always": it describes the market, not any one
    // competitor, which keeps the table fair and defensible.
    comparison: {
      title: 'What to check on any awning quote',
      subtitle: 'Seven questions worth asking any supplier, and the AW01\'s answer to each.',
      columns: ['What to check', 'AW01', 'Often seen elsewhere'],
      rows: [
        ['Wind rating', 'Class 3 (highest), independently tested', 'Class 1 or 2, or not stated'],
        ['Wind sensor', 'Included', 'Optional extra, around $150'],
        ['Fabric', 'Sauleda acrylic, dyed through the fibre', 'PU-coated polyester'],
        ['Colour choice', `${TOTAL_COLOURWAYS}, one price`, '1 to 3'],
        ['UV rating', `${SAULEDA_FACTS.upfShort}, certified`, 'UPF 50+, self-declared'],
        ['Wall fixings', 'Included', 'Buy your own'],
        ['Install', 'DIY about 1 hour, plugs in', 'Varies, sometimes hard-wired'],
      ],
    },

    // Demonstration clip. Shot on a 6 x 3.5 m unit, cropped from a pillarboxed
    // phone video and run at 2x so the full travel fits in half a minute.
    video: {
      title: 'Watch it extend',
      text: 'A 6 × 3.5 m AW01 opening with the LEDs on, at 2× speed.',
      mp4: '/uploads/content/aw01-demo-6x35.mp4?v=1',
      webm: '/uploads/content/aw01-demo-6x35.webm?v=1',
      poster: '/uploads/content/aw01-demo-6x35-poster.webp?v=1',
    },

    // DIY: brackets, a lift and a plug - about an hour with a helper. The
    // motor ships with a standard plug, so there is no electrical work unless
    // a new power point is needed.
    installation: {
      title: 'Fit it yourself',
      lines: [
        'Arrives assembled and tested, with a plug.',
        `Two brackets (three from ${THIRD_BRACKET_FROM_M} m wide), one lift, one power point. About an hour with a helper.`,
        'Fixings included for concrete, solid brick and timber. Hollow brick needs a chemical anchor from any hardware store.',
        'No power point nearby? That\'s the only time you need an electrician.',
      ],
      links: [
        { to: '/measuring-guide', label: 'How to measure' },
        { to: '/for-installers', label: 'Information for your installer' },
      ],
      video: AW01_INSTALL_VIDEO,
      videoLinkLabel: '3-minute install video',
    },

    // One grouped table of hard numbers. Every value is from the technical
    // datasheet, the installation manual, the Intertek report or the config
    // modules. A number we do not have yet is left out rather than guessed -
    // still missing: weight by size and the pitch range in degrees (the
    // owner does not have them yet, 2026-09-27).
    specs: [
      { group: 'Size', rows: [
        ['Width', '2 to 7 m; custom widths in 0.5 m steps'],
        ['Projection', '1 to 3.5 m (arms of 1, 1.5, 2, 2.5, 3 and 3.5 m)'],
        ['Standard sizes (W × P)', '2 × 1, 3 × 2, 4 × 3, 5 × 3.5, 6 × 3.5 and 7 × 3.5 m'],
        ['Cassette, closed (H × D)', '146 × 236.5 mm'],
      ] },
      { group: 'Frame', rows: [
        ['Material', '6063 aluminium, high-density powder coat'],
        ['Colours', 'Bright white or frosted black'],
        ['Arm joints', 'Thickened stainless steel chain link, heavier springs'],
        ['Pitch', 'Adjustable at each bracket with a 6 mm hex key'],
      ] },
      { group: 'Fabric', rows: [
        ['Material', `Sauleda 100% acrylic, solution-dyed, made in Barcelona`],
        ['UV', `UPF ${UPF_MIN} to ${UPF_MAX}, blocks 99% of UVA, certified to UV STANDARD 801`],
        ['Colours', `${TOTAL_COLOURWAYS} (${SOLIDS} solid, ${STRIPES} stripe), all one price`],
      ] },
      { group: 'Wind', rows: [
        ['Rating', 'EN 13561 Class 3 (Beaufort 6, about 39 to 49 km/h), tested by Intertek at 3000 × 1530 mm'],
        ['Sensor', 'Included; retracts the awning automatically'],
      ] },
      { group: 'Motor', rows: [
        ['Type', '240 V tubular motor, IP67, wireless remote supplied, hand crank backup'],
        ['Power', 'Standard 240 V plug on a 1 m cable, into a nearby power point'],
      ] },
      { group: 'Lighting', rows: [
        ['LED', '24 V warm white (3000K) strips along the cassette and each arm, 15 W/m, dimmable, switched separately'],
      ] },
      { group: 'Install', rows: [
        ['People', `Two, or three from ${THREE_PEOPLE_FROM_M} m wide`],
        ['Brackets', `Two, set 6.5 cm in from each end; a third in the middle from ${THIRD_BRACKET_FROM_M} m wide`],
        ['Fixings', `${ANCHORS_PER_BRACKET} anchors per bracket supplied, for concrete, solid brick and timber`],
        ['Mounting', 'Wall or soffit'],
        ['Mounting height', `About ${RECOMMENDED_MOUNTING_HEIGHT_M} m recommended; the front bar must clear doors and paths at full projection`],
        ['Clearances', '8 cm at each end of the cassette; 10 cm from any corner'],
      ] },
      { group: 'Warranty', rows: [
        ['Warranty', `${AWNING_WARRANTY_YEARS} years on ${WARRANTY_COVER.awning.covers}, from the ${WARRANTY_STARTS_FROM}`],
        ['Motor and electrical', 'Manufacturer warranties'],
        ['Expected life', `${AWNING_SERVICE_LIFE_YEARS}+ years for the awning and its fabric (not a warranty term)`],
      ] },
    ],

    // Real reviews only - never placeholders. Until there are some, the block
    // shows photos of AW01s on customers' homes with the suburb. To add
    // reviews, fill `items` with { name, suburb, rating, text, image? } from
    // Google, and set `rating`, `count` and `url` (the Google listing). The
    // star rating then also appears in the buy box.
    reviews: {
      title: 'Reviews',
      rating: null,
      count: null,
      url: null,
      items: [],
      installs: [
        { image: '/uploads/content/project-aw01-1-extended.webp', suburb: 'Rowville' },
        { image: '/uploads/content/project-aw01-2.webp', suburb: 'Rowville' },
        { image: '/uploads/content/project-aw01-3.webp', suburb: 'Rowville' },
      ],
    },

    // Product FAQ. Grouped; each answer is a single plain paragraph. The
    // quick-question form sits under the last group.
    faq: [
      { group: 'Installation', items: [
        { q: 'Can I install it myself?', a: `Yes, and most customers do. The AW01 arrives assembled, tested and fitted with a plug, and the install is ${BRACKET_COUNT}, a lift and a power point - about an hour with a helper, following the manual and the video. Two people for the lift, three if it is ${THREE_PEOPLE_FROM_M} m wide or more. If you would rather not, any handyman can fit it.` },
        { q: 'What kind of wall does it need?', a: 'A solid one, and the right anchor for it. Concrete, solid brick and structural timber take the supplied anchors directly. Hollow brick needs a chemical anchor from any hardware store. Rendered or clad walls are fixed through the surface into the structure behind. If you are unsure what your wall is, drill a small pilot hole first and see what you are into.' },
        { q: 'Where do the brackets go?', a: `Bracket positions are set by the arm bases for the width you order, 6.5 cm in from each end of the cassette, with a third in the middle on awnings ${THIRD_BRACKET_FROM_M} m wide or more. Allow at least 8 cm clear at each end and 10 cm from any internal or external corner. The wall has to take the load at those exact points, not just somewhere along it.` },
        { q: 'How many people does it take?', a: `At least two. Three for awnings ${THREE_PEOPLE_FROM_M} m wide or more, or for any lift above shoulder height.` },
        { q: 'Are fixings included?', a: `Yes. ${ANCHORS_PER_BRACKET} anchors per bracket are in the box, for concrete, solid brick and timber - eight for a two-bracket awning, twelve from ${THIRD_BRACKET_FROM_M} m wide. For hollow brick, pick up a chemical anchor from any hardware store.` },
        { q: 'How high should it be mounted?', a: `About ${RECOMMENDED_MOUNTING_HEIGHT_M} m suits most homes, but your doors, windows and headroom decide it. The awning drops as it extends, so the front bar at full projection is the lowest point. Set the height so paths, doors and windows stay clear there. Our measuring guide walks through it.` },
        { q: 'Can it mount to a ceiling or soffit?', a: 'Yes. Wall mounting is standard, and a soffit bracket lets it hang from a ceiling or eave instead. On a soffit the fixings go into structural timber or steel, not the lining board, and the bracket has to clear the fascia when the awning is closed.' },
      ] },
      { group: 'Power and operation', items: [
        { q: 'Do I need an electrician?', a: 'Usually not. The motor comes with a standard plug on a 1 m cable, so if there is a power point within a metre of the motor end of the cassette you plug it in and you are done. You only need an electrician if there is no power point nearby and you want one installed - the awning itself needs no wiring.' },
        { q: 'What happens in a power cut?', a: 'A hand crank is in the box. You can close the awning by hand while the power is off.' },
        { q: 'Does it have a wind sensor?', a: 'Yes, one is included. It closes the awning on its own when the wind picks up, so you do not have to be home to protect it.' },
        { q: 'Is the LED lighting built in?', a: 'Yes. Warm-white (3000K) 24 V LED strips run along the cassette and along each arm, 15 W per metre, and are powered from the unit, so there is no separate wiring to run. They are switched separately from the awning and are dimmable.' },
      ] },
      { group: 'Weather and care', items: [
        { q: 'How much wind can it take?', a: 'The AW01 is wind tested by Intertek to EN 13561 Class 3, equivalent to Beaufort force 6, roughly 39 to 49 km/h. That result applies to the tested 3000 x 1530 mm unit and does not extend to larger sizes. Retract it above that, and always in hail or snow.' },
        { q: 'Can I leave it out in the wind?', a: 'The sensor retracts it in strong wind. Retract it yourself for storms or hail.' },
        { q: 'Can I leave it out in the rain?', a: 'Yes. The motor is rated IP67, so rain does not harm it, and with the pitch set for run-off the water drains off the fabric. Retract it for storms, hail or strong wind - the wind sensor does that part for you.' },
        { q: 'Will the fabric fade?', a: `Sauleda acrylic is dyed through the fibre, so the colour does not wash out of it. It is covered by our ${AWNING_WARRANTY_YEARS}-year warranty like the rest of the awning, and with normal care you can expect ${AWNING_SERVICE_LIFE_YEARS} years or more from it.` },
        { q: 'Will it go mouldy?', a: 'Acrylic sheds water rather than holding it, and the cassette closes the fabric away dry. Clear leaves, debris and pooled water from the fabric regularly and it stays that way.' },
        { q: 'How do I clean it?', a: 'At least once a year, with a soft sponge or cloth, water and a neutral pH detergent. Do not use acidic or alkaline cleaners. A pressure washer is fine at a sensible distance with a wide nozzle, low pressure and moderate temperature. Let the fabric dry before retracting.' },
        { q: 'Does it work in very cold weather?', a: 'In very low temperatures the motor may stop operating. Do not force it. Wait for it to warm up rather than cranking against a stalled motor.' },
      ] },
      { group: 'Sizes, warranty and council', items: [
        { q: 'What sizes does it come in?', a: 'Six standard sizes: 2 x 1, 3 x 2, 4 x 3, 5 x 3.5, 6 x 3.5 and 7 x 3.5 m. Custom sizes are made to order, in 0.5 m steps of width.' },
        { q: 'What size do I need?', a: 'Measure the area you want shaded, not just the wall. Pick a projection that covers your seating with a little to spare, then check the available width at the mounting height and that the front bar clears your doors and headroom at full projection. Our measuring guide has the details.' },
        { q: 'What\'s covered by the warranty?', a: `${WARRANTY_COVER.awning.summary} The motor and electrical parts carry their manufacturers' warranties. The awning is built to last ${AWNING_SERVICE_LIFE_YEARS} years or more - that is its expected life, not a warranty term. Parts and re-covers are available after the warranty ends. Storm damage, misuse and unsuitable fixings are not covered.` },
        { q: 'How long until it arrives?', a: `A size and colour we hold in stock ships ${LEAD_STOCK} - the stock line at the top of this page shows what is in stock right now. Everything else is made to order: ${LEAD_STANDARD} up to ${STANDARD_SIZE_MAX_M} m wide and deep, ${LEAD_LARGE} above that, custom sizes included.` },
        { q: 'Do I need council approval?', a: 'For a retractable awning on the wall of a private home in Melbourne, generally no. It changes if the property is in a heritage overlay, the awning overhangs a public footpath, or the building is strata or commercial. Check with your council or a building surveyor if any of those apply.' },
      ] },
    ],
  },
};

export function getProductContent(slug) {
  return PRODUCT_CONTENT[slug] || null;
}
